import { Injectable } from '@angular/core';
import { Conversation } from '../services/ai.service';
import { BehaviorSubject, Subject } from 'rxjs';

export interface ConversationReadyEvent {
  projectId: string;
  reportType: string;
}

export interface PendingConversationBootstrap {
  projectId: string;
  reportType: string;
  conversation: Conversation;
  prompt: string;
}

/**
 * Shared service to signal when the onboarding has pending requests.
 * Used by the dashboard to display the beforeunload warning to the user when trying to reload/exit.
 */
@Injectable({
  providedIn: 'root',
})
export class OnboardingPendingService {
  onboardingPending = false;
  conversationPending = false;
  private readonly pendingConversationBootstraps = new Map<string, PendingConversationBootstrap>();
  private readonly conversationPendingState = new BehaviorSubject<boolean>(false);
  private readonly conversationReadyState = new Subject<ConversationReadyEvent>();
  readonly conversationPending$ = this.conversationPendingState.asObservable();
  readonly conversationReady$ = this.conversationReadyState.asObservable();

  get hasPendingRequests(): boolean {
    return this.onboardingPending || this.conversationPending;
  }

  setOnboardingPending(value: boolean): void {
    this.onboardingPending = value;
  }

  setConversationPending(value: boolean): void {
    this.conversationPending = value;
    this.conversationPendingState.next(value);
  }

  notifyConversationReady(event: ConversationReadyEvent): void {
    this.conversationReadyState.next(event);
  }

  setPendingConversationBootstrap(bootstrap: PendingConversationBootstrap): void {
    this.pendingConversationBootstraps.set(bootstrap.projectId, bootstrap);
  }

  consumePendingConversationBootstrap(projectId: string): PendingConversationBootstrap | null {
    const bootstrap = this.pendingConversationBootstraps.get(projectId) ?? null;
    if (bootstrap) {
      this.pendingConversationBootstraps.delete(projectId);
    }
    return bootstrap;
  }

  clearAll(): void {
    this.onboardingPending = false;
    this.conversationPending = false;
    this.pendingConversationBootstraps.clear();
    this.conversationPendingState.next(false);
  }
}
